import { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import type { PayrollRunRow, PayslipRow } from '@/lib/types';
import { titleCase } from '@/lib/format';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const inr = (v: number | string | null) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const num = (v: number | string | null) => Number(v || 0);

function StatusPill({ value }: { value: string }) {
  const v = (value || '').toLowerCase();
  const style = v === 'paid' ? 'bg-[#E6F4EC] text-[#1B8A4E]' : v === 'finalized' ? 'bg-[#E8F0FE] text-[#1A73E8]' : 'bg-[#FFF8E1] text-[#E8A317]';
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${style}`}>{titleCase(value)}</span>;
}

export default function PayrollPage() {
  const now = new Date();
  const { data: runs, loading, reload } = useApiData<PayrollRunRow[]>(() => api.payrollRuns());
  const [showGen, setShowGen] = useState(false);
  const [genMonth, setGenMonth] = useState(now.getMonth() + 1);
  const [genYear, setGenYear] = useState(now.getFullYear());
  const [genDays, setGenDays] = useState('');
  const [saving, setSaving] = useState(false);
  const [genError, setGenError] = useState('');
  const [openRun, setOpenRun] = useState<number | null>(null);

  const generate = async () => {
    setSaving(true); setGenError('');
    try {
      await api.generatePayroll({ periodMonth: genMonth, periodYear: genYear, workingDays: genDays ? Number(genDays) : undefined });
      setShowGen(false); setGenDays(''); await reload();
    } catch (cause) {
      setGenError(cause instanceof Error ? cause.message : 'Could not generate payroll.');
    } finally { setSaving(false); }
  };

  const list = runs ?? [];

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#3C3C3C]">Payroll</h2>
            <p className="text-sm text-gray-500 mt-1">Generate monthly payroll from salary structures and attendance. Salaries are set per employee in Staff HR.</p>
          </div>
          <button onClick={() => setShowGen(true)} className="px-4 py-2.5 bg-[#0B6E77] text-white text-sm font-semibold rounded-lg hover:bg-[#085258] cursor-pointer whitespace-nowrap"><i className="ri-add-line mr-1" />Generate payroll</button>
        </div>

        <div className="bg-white border border-[#E1E5EA] rounded-xl overflow-hidden">
          {loading ? <div className="p-10 text-center text-sm text-gray-400">Loading…</div>
            : list.length === 0 ? <div className="p-12 text-center text-sm text-gray-400">No payroll runs yet. Generate one for the current month to get started.</div>
            : <div className="overflow-x-auto"><table className="w-full min-w-[720px]">
              <thead><tr className="bg-gray-50 text-left text-[11px] uppercase text-gray-400"><th className="px-4 py-3">Period</th><th className="px-4 py-3">Employees</th><th className="px-4 py-3">Working days</th><th className="px-4 py-3">Total net pay</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
              <tbody className="divide-y divide-gray-50">{list.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 text-sm font-semibold text-gray-800">{MONTHS[r.periodMonth - 1]} {r.periodYear}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{r.employees}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{r.workingDays}</td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-800">{inr(r.totalNet)}</td>
                  <td className="px-4 py-4"><StatusPill value={r.status} /></td>
                  <td className="px-4 py-4 text-right"><button onClick={() => setOpenRun(r.id)} className="text-sm text-[#0B6E77] font-medium hover:underline cursor-pointer">View payslips</button></td>
                </tr>
              ))}</tbody>
            </table></div>}
        </div>
      </div>

      {showGen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowGen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-[#3C3C3C]">Generate payroll</h3>
            <p className="text-xs text-gray-500 mt-1">Creates a draft run and a payslip for every active employee, pro-rated by attendance and approved paid leave.</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <label className="text-sm">Month<select value={genMonth} onChange={(e) => setGenMonth(Number(e.target.value))} className="mt-1 w-full border border-[#E1E5EA] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0B6E77]">{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select></label>
              <label className="text-sm">Year<input type="number" value={genYear} onChange={(e) => setGenYear(Number(e.target.value))} className="mt-1 w-full border border-[#E1E5EA] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0B6E77]" /></label>
            </div>
            <label className="text-sm block mt-3">Working days <span className="text-gray-400">(optional — auto-calculated from calendar minus Sundays &amp; holidays)</span><input type="number" value={genDays} onChange={(e) => setGenDays(e.target.value)} placeholder="Auto" className="mt-1 w-full border border-[#E1E5EA] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0B6E77]" /></label>
            {genError && <p className="text-xs text-[#D93025] mt-3">{genError}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowGen(false)} className="px-4 py-2 text-sm text-gray-600 rounded-lg hover:bg-gray-100 cursor-pointer">Cancel</button>
              <button onClick={generate} disabled={saving} className="px-4 py-2 bg-[#0B6E77] text-white text-sm font-semibold rounded-lg hover:bg-[#085258] disabled:opacity-60 cursor-pointer">{saving ? 'Generating…' : 'Generate'}</button>
            </div>
          </div>
        </div>
      )}

      {openRun !== null && <PayrollRunDrawer runId={openRun} onClose={() => setOpenRun(null)} onChanged={reload} />}
    </DashboardLayout>
  );
}

function PayrollRunDrawer({ runId, onClose, onChanged }: { runId: number; onClose: () => void; onChanged: () => void }) {
  const { data, loading, reload } = useApiData(() => api.payrollRun(runId), [runId]);
  const [working, setWorking] = useState('');
  const run = data?.run;
  const slips = data?.payslips ?? [];

  const setRunStatus = async (status: string) => {
    setWorking(status);
    try { await api.updatePayrollRun(runId, status); await reload(); onChanged(); } catch { /* ignore */ } finally { setWorking(''); }
  };
  const togglePaid = async (slip: PayslipRow) => {
    try { await api.updatePayslip(slip.id, slip.status === 'paid' ? 'unpaid' : 'paid'); await reload(); onChanged(); } catch { /* ignore */ }
  };

  const printSlip = (slip: PayslipRow) => {
    if (!run) return;
    const rows = [
      ['Basic', slip.basic], ['HRA', slip.hra], ['Allowances', slip.allowances],
      ['Gross', slip.gross], ['Deductions', `- ${inr(slip.deductions)}`], ['Loss of pay', `- ${inr(slip.lopAmount)}`],
    ];
    const html = `<html><head><title>Payslip ${slip.employeeCode || ''}</title><style>
      body{font-family:Arial,sans-serif;color:#222;padding:32px;max-width:640px;margin:auto}
      h1{color:#0B6E77;font-size:20px;margin:0 0 4px} .muted{color:#888;font-size:12px}
      table{width:100%;border-collapse:collapse;margin-top:16px} td{padding:8px;border-bottom:1px solid #eee;font-size:14px}
      .net{background:#0B6E77;color:#fff;font-weight:bold;font-size:16px;padding:12px;border-radius:8px;margin-top:16px;display:flex;justify-content:space-between}
    </style></head><body>
      <h1>Payslip</h1>
      <div class="muted">${MONTHS[run.periodMonth - 1]} ${run.periodYear}</div>
      <table>
        <tr><td>Employee</td><td style="text-align:right"><b>${slip.employeeName || ''}</b> (${slip.employeeCode || ''})</td></tr>
        <tr><td>Working / Present / Paid leave / LOP days</td><td style="text-align:right">${num(slip.workingDays)} / ${num(slip.presentDays)} / ${num(slip.paidLeaveDays)} / ${num(slip.lopDays)}</td></tr>
        ${rows.map(([k, v]) => `<tr><td>${k}</td><td style="text-align:right">${typeof v === 'string' ? v : inr(v)}</td></tr>`).join('')}
      </table>
      <div class="net"><span>Net pay</span><span>${inr(slip.netPay)}</span></div>
      <p class="muted" style="margin-top:24px">This is a computer-generated payslip.</p>
    </body></html>`;
    const w = window.open('', '_blank', 'noopener,width=720,height=800');
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[#F8F9FB] w-full max-w-3xl h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#E1E5EA] px-6 py-4 flex items-center justify-between z-10">
          <div><h3 className="text-base font-semibold text-[#3C3C3C]">{run ? `${MONTHS[run.periodMonth - 1]} ${run.periodYear} payroll` : 'Payroll'}</h3>{run && <p className="text-xs text-gray-500 mt-0.5">Working days: {run.workingDays} · <StatusPill value={run.status} /></p>}</div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500 text-lg" /></button>
        </div>

        <div className="p-6 space-y-4">
          {run && run.status !== 'paid' && (
            <div className="flex gap-2">
              {run.status === 'draft' && <button onClick={() => setRunStatus('finalized')} disabled={!!working} className="px-4 py-2 bg-[#1A73E8] text-white text-xs font-semibold rounded-lg cursor-pointer disabled:opacity-60">Finalize run</button>}
              <button onClick={() => setRunStatus('paid')} disabled={!!working} className="px-4 py-2 bg-[#1B8A4E] text-white text-xs font-semibold rounded-lg cursor-pointer disabled:opacity-60">Mark all paid</button>
            </div>
          )}

          {loading ? <div className="p-10 text-center text-sm text-gray-400">Loading…</div>
            : slips.length === 0 ? <div className="p-10 text-center text-sm text-gray-400">No payslips in this run. Add employees with a salary in Staff HR, then regenerate.</div>
            : <div className="bg-white border border-[#E1E5EA] rounded-xl overflow-hidden"><div className="overflow-x-auto"><table className="w-full min-w-[760px]">
              <thead><tr className="bg-gray-50 text-left text-[11px] uppercase text-gray-400"><th className="px-4 py-3">Employee</th><th className="px-4 py-3">Present/LOP</th><th className="px-4 py-3">Gross</th><th className="px-4 py-3">Deduct.</th><th className="px-4 py-3">Net pay</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
              <tbody className="divide-y divide-gray-50">{slips.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3"><p className="text-sm font-medium text-gray-800">{s.employeeName || '—'}</p><p className="text-[11px] text-gray-400">{s.employeeCode}</p></td>
                  <td className="px-4 py-3 text-sm text-gray-500">{num(s.presentDays)} / {num(s.lopDays)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{inr(s.gross)}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{inr(num(s.deductions) + num(s.lopAmount))}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-800">{inr(s.netPay)}</td>
                  <td className="px-4 py-3"><StatusPill value={s.status} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => printSlip(s)} className="text-gray-400 hover:text-[#0B6E77] mr-3 cursor-pointer" title="Print payslip"><i className="ri-printer-line" /></button>
                    <button onClick={() => togglePaid(s)} className="text-xs text-[#0B6E77] font-medium hover:underline cursor-pointer">{s.status === 'paid' ? 'Unpay' : 'Mark paid'}</button>
                  </td>
                </tr>
              ))}</tbody>
            </table></div></div>}
        </div>
      </div>
    </div>
  );
}
